export interface LegalDocument {
  id: string;
  name: string;
  type: "PETITION" | "VAKALATNAMA" | "AFFIDAVIT" | "COURT_ORDER" | "EVIDENCE" | "INVOICE";
  fileSize: string;
  uploadedAt: string;
  status: "Drafted" | "Client Signed" | "Filed in Registry" | "Certified Copy Issued";
  downloadUrl?: string;
}

export interface CourtAppearance {
  date: string;
  court: string;
  courtRoom: string;
  judgeBench: string;
  itemNumber: number;
  purpose: "Admission" | "Arguments" | "Cross Examination" | "Interim Injunction" | "Final Order";
  orderSummary?: string;
}

export interface LegalCase {
  id: number;
  case_number: string; // e.g. "ARB/2026/894" or "CRL.M.C. 142/2026"
  cnr_number: string;  // 16-digit CNR e.g. "DLHC01-098742-2026"
  title: string;       // e.g. "AeroTech Dynamics vs. Union Logistics Corp"
  client_name: string;
  client_email: string;
  client_phone: string;
  opposing_party: string;
  practice_area: 
    | "Corporate & M&A"
    | "Criminal Defense & Bail"
    | "Civil Litigation & Property"
    | "Intellectual Property & Patents"
    | "Arbitration & ADR"
    | "Family Law & Trusts";
  court_name:
    | "Supreme Court of India"
    | "High Court of Delhi"
    | "National Company Law Tribunal (NCLT)"
    | "City Civil Court"
    | "Commercial Court"
    | "District Court";
  court_room: string;
  judge_bench: string;
  next_hearing_date: string;
  item_number: number;
  stage: 
    | "New Case Intake"
    | "Consultation & Retainer"
    | "Drafting & Evidence"
    | "Filed in Court / Listed"
    | "Hearing & Arguments"
    | "Decree & Closed (Won)";
  urgency: "EMERGENCY" | "HIGH" | "NORMAL";
  retainer_amount: number;
  total_billed_amount: number;
  currency: "INR" | "USD";
  lead_counsel: string;
  notes?: string;
  conflict_check_passed: number; // 1 or 0
  documents: LegalDocument[];
  appearances: CourtAppearance[];
  created_at: string;
  updated_at: string;
}

export interface PracticeAreaInfo {
  id: string;
  title: string;
  category: string;
  description: string;
  typicalMatters: string[];
  statutoryFocus: string;
  indicativeRetainerINR: number;
  indicativeRetainerUSD: number;
}

export const PRACTICE_AREAS: PracticeAreaInfo[] = [
  {
    id: "corporate-ma",
    title: "Corporate Mergers, Acquisitions & Contracts",
    category: "Commercial & Transactional Advisory",
    description: "Structuring cross-border joint ventures, shareholder agreements, term-sheet negotiations, and regulatory due diligence under the Companies Act.",
    typicalMatters: ["Share Purchase Agreements", "Board Resolution Protocols", "Foreign Direct Investment Clearance", "Employee Stock Option Trusts"],
    statutoryFocus: "Companies Act 2013 • SEBI Regulations • FEMA",
    indicativeRetainerINR: 75000,
    indicativeRetainerUSD: 1200,
  },
  {
    id: "criminal-bail",
    title: "Criminal Defense, Anticipatory Bail & Economic Offenses",
    category: "Trial & Appellate Criminal Law",
    description: "Urgent 24/7 representation for anticipatory bail, quashing of FIRs under Section 482 CrPC/BNSS, PMLA compliance, and white-collar defense.",
    typicalMatters: ["Special Leave Petitions in SC", "Anticipatory Bail Applications", "Enforcement Directorate Inquiries", "CBI Defense Protocols"],
    statutoryFocus: "Bharatiya Nagarik Suraksha Sanhita (BNSS) • PMLA • IPC",
    indicativeRetainerINR: 125000,
    indicativeRetainerUSD: 2000,
  },
  {
    id: "civil-property",
    title: "Civil Litigation, Title Injunctions & Property Disputes",
    category: "Real Estate & Commercial Law",
    description: "Property title verification, partition suits, recovery of possession, mandatory temporary injunctions, and specific performance of contracts.",
    typicalMatters: ["Title Injunction Suits", "Specific Relief Petitions", "RERA Adjudications", "Mesne Profits & Recovery"],
    statutoryFocus: "Code of Civil Procedure (CPC) • Transfer of Property Act • RERA",
    indicativeRetainerINR: 50000,
    indicativeRetainerUSD: 850,
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property, Patents & Trademark Infringement",
    category: "Technology, Media & IP",
    description: "Global trademark prosecution, patent opposition, copyright licensing, trade secret protection, and ex-parte Anton Piller search-and-seizure orders.",
    typicalMatters: ["Patent Invalidation Actions", "Trademark Passing-Off Injunctions", "Copyright Infringement Notices", "Software Source Code Escrow"],
    statutoryFocus: "Trade Marks Act 1999 • Patents Act 1970 • Copyright Act",
    indicativeRetainerINR: 65000,
    indicativeRetainerUSD: 1050,
  },
  {
    id: "arbitration-adr",
    title: "Commercial Arbitration & Dispute Resolution (ADR)",
    category: "Institutional & Ad-Hoc Arbitration",
    description: "Representation under SIAC, LCIA, and DIAC arbitration rules, Section 9 interim measures, Section 11 arbitrator appointments, and Section 34 enforcement.",
    typicalMatters: ["Emergency Arbitrator Relief", "Enforcement of Foreign Awards", "Infrastructure EPC Dispute Panels", "Shareholder Buyout Claims"],
    statutoryFocus: "Arbitration & Conciliation Act 1996 • New York Convention",
    indicativeRetainerINR: 100000,
    indicativeRetainerUSD: 1650,
  },
  {
    id: "family-trusts",
    title: "Family Law, Private Wealth Trusts & Succession",
    category: "Private Client & Estate Planning",
    description: "Family business succession constitutions, revocable living trusts, probate of wills, mediation, and matrimonial property settlement agreements.",
    typicalMatters: ["Family Wealth Trust Deeds", "Probate of Contested Wills", "Divorce & Custody Settlement", "Partition of Ancestral Assets"],
    statutoryFocus: "Indian Succession Act • Special Marriage Act • Trust Act",
    indicativeRetainerINR: 40000,
    indicativeRetainerUSD: 650,
  },
];

export const INITIAL_BENCHMARK_CASES: Omit<LegalCase, "id" | "created_at" | "updated_at">[] = [
  {
    case_number: "ARB/2026/894",
    cnr_number: "DLHC01-084920-2026",
    title: "AeroTech Dynamics vs. Union Logistics Corp",
    client_name: "Vikramaditya Singhania",
    client_email: "v.singhania@aerotech.in",
    client_phone: "+91 98112 45890",
    opposing_party: "Union Logistics Global Holdings Ltd",
    practice_area: "Arbitration & ADR",
    court_name: "High Court of Delhi",
    court_room: "Court Room 14 (Commercial Division)",
    judge_bench: "Hon'ble Mr. Justice Rajiv Shakdher",
    next_hearing_date: "2026-09-08",
    item_number: 12,
    stage: "Hearing & Arguments",
    urgency: "HIGH",
    retainer_amount: 150000,
    total_billed_amount: 325000,
    currency: "INR",
    lead_counsel: "Adv. Johnson & Senior Counsel Deshmukh",
    notes: "Section 9 interim injunction granted restraining invocation of bank guarantee worth ₹14.8 Cr. Cross-examination of technical expert listed next.",
    conflict_check_passed: 1,
    documents: [
      {
        id: "doc-1",
        name: "Section_9_Interim_Petition.pdf",
        type: "PETITION",
        fileSize: "2.4 MB",
        uploadedAt: "2026-08-12",
        status: "Filed in Registry",
      },
      {
        id: "doc-2",
        name: "Signed_Vakalatnama_Singhania.pdf",
        type: "VAKALATNAMA",
        fileSize: "840 KB",
        uploadedAt: "2026-08-10",
        status: "Client Signed",
      },
      {
        id: "doc-3",
        name: "Interim_Order_Status_Quo_Bank_Guarantee.pdf",
        type: "COURT_ORDER",
        fileSize: "1.1 MB",
        uploadedAt: "2026-08-19",
        status: "Certified Copy Issued",
      },
    ],
    appearances: [
      {
        date: "2026-08-19",
        court: "High Court of Delhi",
        courtRoom: "Court Room 14",
        judgeBench: "Hon'ble Mr. Justice Rajiv Shakdher",
        itemNumber: 8,
        purpose: "Interim Injunction",
        orderSummary: "Notice issued to respondent. Interim stay granted on encashment of performance bank guarantee till next date.",
      },
    ],
  },
  {
    case_number: "BAIL.APPL. 312/2026",
    cnr_number: "DLHC01-091402-2026",
    title: "State vs. Rajeshwar Goel (Anticipatory Bail)",
    client_name: "Rajeshwar Goel",
    client_email: "r.goel@goelexports.com",
    client_phone: "+91 98808 72190",
    opposing_party: "State of NCT of Delhi / Directorate of Enforcement",
    practice_area: "Criminal Defense & Bail",
    court_name: "High Court of Delhi",
    court_room: "Court Room 4 (Criminal Bench)",
    judge_bench: "Hon'ble Ms. Justice Swarana Kanta Sharma",
    next_hearing_date: "2026-09-04",
    item_number: 4,
    stage: "Hearing & Arguments",
    urgency: "EMERGENCY",
    retainer_amount: 200000,
    total_billed_amount: 450000,
    currency: "INR",
    lead_counsel: "Adv. Johnson",
    notes: "Listed at Item 4 tomorrow for pre-arrest interim protection. Case diary summoned from IO. Medical records annexed.",
    conflict_check_passed: 1,
    documents: [
      {
        id: "doc-4",
        name: "Anticipatory_Bail_Application_438_BNSS.pdf",
        type: "PETITION",
        fileSize: "3.2 MB",
        uploadedAt: "2026-08-28",
        status: "Filed in Registry",
      },
      {
        id: "doc-5",
        name: "Medical_Board_Cardiology_Summary.pdf",
        type: "EVIDENCE",
        fileSize: "1.8 MB",
        uploadedAt: "2026-08-29",
        status: "Drafted",
      },
    ],
    appearances: [
      {
        date: "2026-09-01",
        court: "High Court of Delhi",
        courtRoom: "Court Room 4",
        judgeBench: "Hon'ble Ms. Justice Swarana Kanta Sharma",
        itemNumber: 15,
        purpose: "Arguments",
        orderSummary: "State granted 48 hours to file status report. Matter re-listed at top of board for hearing on interim protection.",
      },
    ],
  },
  {
    case_number: "CS(COMM) 104/2026",
    cnr_number: "DLHC01-076211-2026",
    title: "NovaPharma Ltd vs. ZenBio Formulations",
    client_name: "Dr. Alistair Sterling",
    client_email: "a.sterling@novapharma.co.uk",
    client_phone: "+44 7700 900412",
    opposing_party: "ZenBio Generic Pharmaceuticals India Pvt Ltd",
    practice_area: "Intellectual Property & Patents",
    court_name: "High Court of Delhi",
    court_room: "Court Room 21 (IP Division)",
    judge_bench: "Hon'ble Mr. Justice C. Hari Shankar",
    next_hearing_date: "2026-09-15",
    item_number: 22,
    stage: "Filed in Court / Listed",
    urgency: "HIGH",
    retainer_amount: 3500,
    total_billed_amount: 7200,
    currency: "USD",
    lead_counsel: "Adv. Johnson & IP Senior Patent Counsel",
    notes: "Patent infringement suit regarding sustained-release oncology formulation. Ex-parte ad-interim injunction prayer pending.",
    conflict_check_passed: 1,
    documents: [
      {
        id: "doc-6",
        name: "Plaint_Patent_Infringement_With_Annexures.pdf",
        type: "PETITION",
        fileSize: "14.2 MB",
        uploadedAt: "2026-08-20",
        status: "Filed in Registry",
      },
      {
        id: "doc-7",
        name: "Patent_Grant_Certificate_IN389102.pdf",
        type: "EVIDENCE",
        fileSize: "920 KB",
        uploadedAt: "2026-08-15",
        status: "Certified Copy Issued",
      },
    ],
    appearances: [],
  },
  {
    case_number: "CP(IB)/482/2026",
    cnr_number: "NCLT01-004812-2026",
    title: "Meridian Capital vs. Vertex Infrastructure",
    client_name: "Rohan Mehra",
    client_email: "rmehra@meridiancap.com",
    client_phone: "+91 99100 81234",
    opposing_party: "Vertex Infrastructure & Real Estate Ltd",
    practice_area: "Corporate & M&A",
    court_name: "National Company Law Tribunal (NCLT)",
    court_room: "Court 2 (Principal Bench, New Delhi)",
    judge_bench: "Hon'ble President & Member Technical",
    next_hearing_date: "2026-09-11",
    item_number: 18,
    stage: "Drafting & Evidence",
    urgency: "NORMAL",
    retainer_amount: 85000,
    total_billed_amount: 85000,
    currency: "INR",
    lead_counsel: "Adv. Johnson",
    notes: "Section 7 IBC petition for financial debt default of ₹28.5 Cr. Demand notice issued under Form 1. Rejoinder in preparation.",
    conflict_check_passed: 1,
    documents: [
      {
        id: "doc-8",
        name: "Section_7_Insolvency_Petition_Draft.pdf",
        type: "PETITION",
        fileSize: "4.8 MB",
        uploadedAt: "2026-08-25",
        status: "Drafted",
      },
      {
        id: "doc-9",
        name: "NeSL_Record_Of_Default_Certificate.pdf",
        type: "EVIDENCE",
        fileSize: "640 KB",
        uploadedAt: "2026-08-24",
        status: "Certified Copy Issued",
      },
    ],
    appearances: [],
  },
  {
    case_number: "CS(OS) 289/2026",
    cnr_number: "DLHC01-094118-2026",
    title: "Oberoi Heritage Trusts vs. DLF Gateway Towers",
    client_name: "Maharani Gayatridevi Heritage Trust",
    client_email: "trustees@oberoiheritage.org",
    client_phone: "+91 98200 11928",
    opposing_party: "DLF Gateway Estates Consortium",
    practice_area: "Civil Litigation & Property",
    court_name: "City Civil Court",
    court_room: "Court Room 6 (Senior Civil Judge)",
    judge_bench: "Sh. Anupam Gupta, Ld. District Judge",
    next_hearing_date: "2026-09-22",
    item_number: 9,
    stage: "Consultation & Retainer",
    urgency: "NORMAL",
    retainer_amount: 60000,
    total_billed_amount: 60000,
    currency: "INR",
    lead_counsel: "Adv. Johnson",
    notes: "Suit for declaration of ancestral title and perpetual injunction on prime 4.2-acre commercial parcel. Revenue records verified.",
    conflict_check_passed: 1,
    documents: [
      {
        id: "doc-10",
        name: "Title_Search_Report_1952_to_2026.pdf",
        type: "EVIDENCE",
        fileSize: "8.1 MB",
        uploadedAt: "2026-08-18",
        status: "Certified Copy Issued",
      },
    ],
    appearances: [],
  },
  {
    case_number: "MAT.APP. 48/2026",
    cnr_number: "DLHC01-051890-2026",
    title: "Kapoor Family Trust Constitutional Settlement",
    client_name: "Siddharth Kapoor",
    client_email: "s.kapoor@kholdings.sg",
    client_phone: "+65 8129 4012",
    opposing_party: "Joint Heirs & Beneficiaries",
    practice_area: "Family Law & Trusts",
    court_name: "Commercial Court",
    court_room: "Mediation Centre Room 3",
    judge_bench: "Ld. Mediator Judge (Retd.)",
    next_hearing_date: "2026-09-18",
    item_number: 2,
    stage: "Consultation & Retainer",
    urgency: "NORMAL",
    retainer_amount: 1500,
    total_billed_amount: 1500,
    currency: "USD",
    lead_counsel: "Adv. Johnson",
    notes: "Structuring multi-jurisdictional private family trust deed for assets across London, Singapore, and Mumbai. Mediation in progress.",
    conflict_check_passed: 1,
    documents: [
      {
        id: "doc-11",
        name: "Private_Family_Trust_Deed_Charter.pdf",
        type: "PETITION",
        fileSize: "1.9 MB",
        uploadedAt: "2026-08-22",
        status: "Drafted",
      },
    ],
    appearances: [],
  },
  {
    case_number: "INQ/2026/049",
    cnr_number: "DLHC01-099412-2026",
    title: "Quantum Cloud AI vs. CyberTech Solutions (Trade Secrets)",
    client_name: "Ananya Deshmukh",
    client_email: "ananya@quantumcloud.ai",
    client_phone: "+91 97118 33490",
    opposing_party: "Former Chief Technology Officer & CyberTech",
    practice_area: "Intellectual Property & Patents",
    court_name: "High Court of Delhi",
    court_room: "Court Room 18 (Commercial Bench)",
    judge_bench: "Hon'ble Mr. Justice Prathiba M. Singh",
    next_hearing_date: "2026-09-09",
    item_number: 7,
    stage: "New Case Intake",
    urgency: "HIGH",
    retainer_amount: 75000,
    total_billed_amount: 0,
    currency: "INR",
    lead_counsel: "Adv. Johnson",
    notes: "Client alleges ex-CTO downloaded proprietary model weights and source repositories. Preparing urgent Anton Piller ex-parte commission.",
    conflict_check_passed: 1,
    documents: [],
    appearances: [],
  },
  {
    case_number: "ARB/2025/112",
    cnr_number: "DLHC01-042890-2025",
    title: "Sterling Real Estate Developers vs. Metro Urban Rail",
    client_name: "Devendra Singhal",
    client_email: "dsinghal@sterlingrealty.in",
    client_phone: "+91 98101 22910",
    opposing_party: "State Rapid Rail Transport Corporation",
    practice_area: "Arbitration & ADR",
    court_name: "Supreme Court of India",
    court_room: "Court Room 2",
    judge_bench: "Hon'ble Chief Justice Bench",
    next_hearing_date: "2026-07-28",
    item_number: 1,
    stage: "Decree & Closed (Won)",
    urgency: "NORMAL",
    retainer_amount: 250000,
    total_billed_amount: 680000,
    currency: "INR",
    lead_counsel: "Adv. Johnson & Senior Counsel Dave",
    notes: "Final Arbitral Award upheld by Supreme Court. ₹38.4 Cr claim decreed in favor of client. Execution warrant executed.",
    conflict_check_passed: 1,
    documents: [
      {
        id: "doc-12",
        name: "Final_Arbitral_Award_Enforcement_Decree.pdf",
        type: "COURT_ORDER",
        fileSize: "6.8 MB",
        uploadedAt: "2026-07-30",
        status: "Certified Copy Issued",
      },
    ],
    appearances: [
      {
        date: "2026-07-28",
        court: "Supreme Court of India",
        courtRoom: "Court Room 2",
        judgeBench: "Hon'ble Chief Justice Bench",
        itemNumber: 1,
        purpose: "Final Order",
        orderSummary: "Special Leave Petition dismissed. High Court judgment upholding ₹38.4 Cr arbitral award affirmed.",
      },
    ],
  },
];
